import React, { useState, useEffect, useRef } from 'react';
import './Dashboard.css';
import { useNavigate } from 'react-router-dom';
import DatenschutzModal from './DatenschutzModal';
import CookieBanner from './CookieBanner';
import { useUser } from '../UserContext';

function Dashboard() {
  const navigate = useNavigate();
  const { user: currentUser } = useUser();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showPrivacyManagement, setShowPrivacyManagement] = useState(false);
  const [privacyAcceptedUsers, setPrivacyAcceptedUsers] = useState([]);
  const [showPrivacyEditor, setShowPrivacyEditor] = useState(false);
  const [privacyContent, setPrivacyContent] = useState('');
  const dropdownRef = useRef(null);

  // Prüft, ob der Benutzer SuperAdmin ist
  const isSuperAdmin = () => {
    return currentUser && currentUser.role === "superadmin";
  };

  // Lade Benutzer die Datenschutz akzeptiert haben
  useEffect(() => {
    const loadPrivacyAcceptedUsers = () => {
      const acceptedUsers = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('privacyAccepted_')) {
          const userEmail = key.replace('privacyAccepted_', '');
          const acceptedDate = localStorage.getItem(key);
          acceptedUsers.push({
            email: userEmail,
            acceptedDate: acceptedDate
          });
        }
      }
      setPrivacyAcceptedUsers(acceptedUsers);
    };

    if (isSuperAdmin()) {
      loadPrivacyAcceptedUsers();
      // Lade den aktuellen Datenschutz-Inhalt
      const savedContent = localStorage.getItem('privacyContent');
      if (savedContent) {
        setPrivacyContent(savedContent);
      } else {
        // Standard-Inhalt falls noch nichts gespeichert ist
        setPrivacyContent(`# Datenschutzerklärung

## Verantwortlicher
OK POWER PERSONEN-OBJEKT-WERKSCHUTZ GMBH
Verantwortlich für die Datenverarbeitung auf dieser Website

## Verarbeitete Daten
- E-Mail-Adressen
- Passwörter (verschlüsselt)
- Projektinformationen
- Dienstplan-Daten
- Benutzerrollen und Berechtigungen

## Zweck der Verarbeitung
- Authentifizierung und Autorisierung
- Projektverwaltung
- Dienstplan-Erstellung
- Kommunikation

## Rechtsgrundlage
Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse)

## Speicherdauer
- Benutzerdaten: Bis zur Löschung des Accounts
- Projektdaten: Bis zur Projektbeendigung
- Dienstplan-Daten: 7 Jahre (gesetzliche Aufbewahrungspflicht)

## Ihre Rechte
- Auskunft (Art. 15 DSGVO)
- Berichtigung (Art. 16 DSGVO)
- Löschung (Art. 17 DSGVO)
- Einschränkung (Art. 18 DSGVO)
- Datenübertragbarkeit (Art. 20 DSGVO)
- Widerspruch (Art. 21 DSGVO)

## Kontakt
Bei Fragen wenden Sie sich an: datenschutz@power-brandschutz.de`);
      }
    }
  }, [currentUser]);

  const handleEmergencyLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const handleShowPrivacy = () => {
    setShowPrivacyModal(true);
    setShowDropdown(false);
  };

  const handleAcceptPrivacy = () => {
    if (currentUser && currentUser.email) {
      localStorage.setItem(`privacyAccepted_${currentUser.email}`, new Date().toISOString());
      setShowPrivacyModal(false);
      // Aktualisiere die Liste der akzeptierten Benutzer
      setPrivacyAcceptedUsers(prev => [...prev, {
        email: currentUser.email,
        acceptedDate: new Date().toISOString()
      }]);
    }
  };

  const handleShowPrivacyManagement = () => {
    setShowPrivacyManagement(true);
    setShowDropdown(false);
  };

  const handleResetPrivacyAcceptance = (userEmail) => {
    if (window.confirm(`Möchten Sie die Datenschutz-Akzeptierung für ${userEmail} zurücksetzen?`)) {
      localStorage.removeItem(`privacyAccepted_${userEmail}`);
      setPrivacyAcceptedUsers(prev => prev.filter(user => user.email !== userEmail));
    }
  };

  const handleShowPrivacyEditor = () => {
    setShowPrivacyEditor(true);
    setShowDropdown(false);
  };

  const handleSavePrivacyContent = () => {
    localStorage.setItem('privacyContent', privacyContent);
    setShowPrivacyEditor(false);
    alert('Datenschutzerklärung wurde erfolgreich gespeichert!');
  };

  // Click outside handler für Dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div className="dashboard">
      {/* Dropdown-Menü Button - nur für SuperAdmins */}
      {isSuperAdmin() && (
        <div ref={dropdownRef} style={{position: 'absolute', top: 16, right: 16, zIndex: 9999}}>
        <button 
          style={{
            background: '#c0392b', 
            color: 'white', 
            border: 'none', 
            borderRadius: 6, 
            padding: '8px 16px', 
            fontWeight: 600, 
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }} 
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <i className="fas fa-cog"></i>
          Einstellungen
          <i className={`fas fa-chevron-${showDropdown ? 'up' : 'down'}`}></i>
        </button>
        
        {/* Dropdown-Menü */}
        {showDropdown && (
          <div style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: '200px',
            zIndex: 10000
          }}>
            <button
              onClick={handleShowPrivacy}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#2E7D32',
                borderBottom: '1px solid #eee'
              }}
            >
              <i className="fas fa-shield-alt"></i>
              Datenschutzerklärung
            </button>

                      <button
                        onClick={handleShowPrivacyEditor}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: 'none',
                          background: 'none',
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          color: '#FF9800',
                          borderBottom: '1px solid #eee'
                        }}
                      >
                        <i className="fas fa-edit"></i>
                        Datenschutz bearbeiten
                      </button>

                      <button
                        onClick={handleShowPrivacyManagement}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: 'none',
                          background: 'none',
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          color: '#1976D2',
                          borderBottom: '1px solid #eee'
                        }}
                      >
                        <i className="fas fa-users-cog"></i>
                        Datenschutz-Verwaltung
                        <span style={{
                          marginLeft: 'auto',
                          fontSize: '12px',
                          backgroundColor: '#1976D2',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '10px'
                        }}>
                          {privacyAcceptedUsers.length}
                        </span>
                      </button>
            
            <button
              onClick={handleEmergencyLogout}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#c0392b'
              }}
            >
              <i className="fas fa-sign-out-alt"></i>
              Notfall-Logout
            </button>
          </div>
        )}
        </div>
      )}
      <h1>Willkommen in der Brandschutz-App</h1>
      
      <div className="welcome-message">
        <h2>Herzlich willkommen, liebe Kolleginnen und Kollegen,</h2>
        
        <p>
          die Brandschutz-App wurde entwickelt, um die tägliche Arbeit im Projekt effizienter und strukturierter zu gestalten. 
          Wenn ihr Anregungen oder Verbesserungsvorschläge habt, wendet euch gerne an Matthias Walder oder Sascha Gnüchwitz.
        </p>

        <h3>Neuigkeiten zur Brandschutz-App</h3>
        
        <div className="news-section">
          <h4>Bereich: Freiwünsche / Urlaub</h4>
          <ul>
            <li>Freiwünsche: Ab sofort muss ein Grund angegeben werden, sonst kann der Antrag nicht eingereicht werden.</li>
            <li>Urlaub: Die Angabe eines Grundes ist optional.</li>
          </ul>

          <h4>Bereich: Enge Räume</h4>
          <ul>
            <li>Optisches Design wurde überarbeitet.</li>
            <li>Der Button "Excel exportieren" wurde entfernt.</li>
            <li>Das Datum-Suchfeld wurde entfernt – stattdessen gibt es jetzt ein zentrales Suchfeld.</li>
            <li>Neu ist ein Dropdown-Menü zur Filterung nach Status.</li>
            <li>Die E-Mail-Vorschau für die Freimessungsbestellung wurde angepasst.</li>
            <li>Der Text wurde überarbeitet – Outlook öffnet sich jetzt wieder automatisch mit der Bestell-E-Mail.</li>
          </ul>
        </div>

        <p className="closing">
          Wir wünschen euch weiterhin viel Erfolg und Freude bei der Nutzung der Brandschutz-App!
        </p>
      </div>

      {/* Modals */}
      <DatenschutzModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        showAcceptButton={true}
        onAccept={handleAcceptPrivacy}
      />

      {/* Datenschutz-Verwaltungs-Modal */}
      {showPrivacyManagement && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '70vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #eee',
              paddingBottom: '10px'
            }}>
              <h2 style={{ margin: 0, color: '#1976D2', fontSize: '20px' }}>
                <i className="fas fa-users-cog"></i> Datenschutz-Verwaltung
              </h2>
              <button
                onClick={() => setShowPrivacyManagement(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: '#1976D2', marginBottom: '10px' }}>
                Benutzer mit Datenschutz-Akzeptierung ({privacyAcceptedUsers.length})
              </h3>
              
              {privacyAcceptedUsers.length === 0 ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                  Noch keine Benutzer haben den Datenschutz akzeptiert.
                </p>
              ) : (
                <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {privacyAcceptedUsers.map((user, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px',
                      border: '1px solid #eee',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      backgroundColor: '#f9f9f9'
                    }}>
                      <div>
                        <div style={{ fontWeight: 'bold', color: '#333' }}>
                          {user.email}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Akzeptiert: {new Date(user.acceptedDate).toLocaleString('de-DE')}
                        </div>
                      </div>
                      <button
                        onClick={() => handleResetPrivacyAcceptance(user.email)}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #c0392b',
                          borderRadius: '4px',
                          backgroundColor: 'transparent',
                          color: '#c0392b',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title="Datenschutz-Akzeptierung zurücksetzen"
                      >
                        <i className="fas fa-undo"></i> Zurücksetzen
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{
              marginTop: '20px',
              paddingTop: '15px',
              borderTop: '1px solid #eee',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button
                onClick={() => setShowPrivacyManagement(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: 'transparent',
                  cursor: 'pointer'
                }}
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
              )}

              {/* Datenschutz-Editor-Modal */}
              {showPrivacyEditor && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10000
                }}>
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '20px',
                    maxWidth: '800px',
                    width: '90%',
                    maxHeight: '80vh',
                    overflow: 'auto',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '20px',
                      borderBottom: '1px solid #eee',
                      paddingBottom: '10px'
                    }}>
                      <h2 style={{ margin: 0, color: '#FF9800', fontSize: '20px' }}>
                        <i className="fas fa-edit"></i> Datenschutzerklärung bearbeiten
                      </h2>
                      <button
                        onClick={() => setShowPrivacyEditor(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '24px',
                          cursor: 'pointer',
                          color: '#666'
                        }}
                      >
                        ×
                      </button>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                        Datenschutzerklärung-Inhalt (Markdown-Format):
                      </label>
                      <textarea
                        value={privacyContent}
                        onChange={(e) => setPrivacyContent(e.target.value)}
                        style={{
                          width: '100%',
                          height: '400px',
                          padding: '15px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontFamily: 'monospace',
                          resize: 'vertical'
                        }}
                        placeholder="Geben Sie hier den Inhalt der Datenschutzerklärung ein..."
                      />
                    </div>

                    <div style={{
                      marginTop: '20px',
                      paddingTop: '15px',
                      borderTop: '1px solid #eee',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '10px'
                    }}>
                      <button
                        onClick={() => setShowPrivacyEditor(false)}
                        style={{
                          padding: '8px 16px',
                          border: '1px solid #ccc',
                          borderRadius: '6px',
                          backgroundColor: 'transparent',
                          cursor: 'pointer'
                        }}
                      >
                        Abbrechen
                      </button>
                      <button
                        onClick={handleSavePrivacyContent}
                        style={{
                          padding: '8px 16px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: '#FF9800',
                          color: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <i className="fas fa-save"></i> Speichern
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <CookieBanner />
            </div>
          );
        }

        export default Dashboard;